import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ClickOutsideListener } from '@ace/core';
import { getEnv } from 'mobx-state-tree';
import { Observer } from 'mobx-react';
import classNames from 'classnames';

import { useDevicePairingUIStore, useIntegrationsUIStore, useUserPermissionsUIStore, useUserProfileUIStore } from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { RoutePaths } from '@core/routes/RoutePaths';
import { Avatar } from '@shared/components/Avatar';
import { DropdownMenu } from '@shared/components/DropdownMenu';
import { OtpModal } from './components/OtpModal';
import { IntegrationsModal } from 'Integrations/components';


import WhiteArrowUpIcon from '@assets/white-arrow-up.svg';
import { messages } from '../../header.messages';
import { useStyles } from './UserMenu.styles';
import { ThemeProps } from '@styles/theme';

export const UserMenu = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const userProfileUIStore = useUserProfileUIStore();
    const devicePairingUIStore = useDevicePairingUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const integrationsUIStore = useIntegrationsUIStore();
    const { navigator } = getEnv<IStoresEnv>(userProfileUIStore);
    const [dropdownOpen, toggleDropdownOpen] = useState(false);

    const signOutUser = async () => {
        await userProfileUIStore.signOut();
        navigator.to(RoutePaths.login);
    };
    const goToProfile = () => {
        toggleDropdownOpen(false);
        navigator.to(RoutePaths.profile);
    };

    const closeOtpModal = () => {
        userProfileUIStore.toggleOtpModalOpened(false);
    };

    const requestOtp = () =>{
        userProfileUIStore.toggleOtpModalOpened(true);
        if (userProfileUIStore.otp == null) {
            userProfileUIStore.requestOtp(null);
        }
    };

    // The dialog itself is rendered by AppLayout, so it stays put as the user navigates.
    const pairDevice = () => {
        toggleDropdownOpen(false);
        devicePairingUIStore.openModal();
    };

    const goToPairedDevices = () => {
        toggleDropdownOpen(false);
        navigator.to(RoutePaths.pairedDevices);
    };

    const openIntegrations = () => {
        toggleDropdownOpen(false);
        integrationsUIStore.toggleModalOpened(true);
    };

    const closeIntegrations = () => {
        integrationsUIStore.toggleModalOpened(false);
    };

    return (
        <section className={styles.wrapper}>
            <span className={styles.userName}>
                {userProfileUIStore.userInfo?.fullName}
            </span>

            <Observer>
                {() => (
                    <OtpModal
                        isOpen={userProfileUIStore.otpModalOpened}
                        onRequestClose={() => closeOtpModal()}
                    />
                )}
            </Observer>

            <Observer>
                {() => (
                    <IntegrationsModal
                        isOpen={integrationsUIStore.isModalOpened}
                        onRequestClose={closeIntegrations}
                    />
                )}
            </Observer>

            <Observer>
                {() => (
                    <Avatar
                        firstName={userProfileUIStore.userInfo?.firstName || ''}
                        lastName={userProfileUIStore.userInfo?.lastName || ''}
                        image={userProfileUIStore.userInfo?.avatar}
                    />
                )}
            </Observer>

            <ClickOutsideListener
                outsideClickIgnoreClass={styles.dropdown}
                onClickOutside={() => toggleDropdownOpen(false)}
            >
                <button
                    onClick={() => toggleDropdownOpen(!dropdownOpen)}
                    className={classNames(styles.button, { [styles.rotated]: dropdownOpen })}
                >
                    <img src={WhiteArrowUpIcon} />
                </button>

                <Observer>
                    {() => {
                        // Read inside the Observer so the entry appears as soon as roles are known,
                        // rather than waiting for whatever re-renders the header next.
                        const canManagePairedDevices = userPermissionsUIStore.isSuperAdmin ||
                            userPermissionsUIStore.isMultiAdmin ||
                            userPermissionsUIStore.isAdmin ||
                            userPermissionsUIStore.isManager;

                        return (
                            <DropdownMenu
                                isOpen={dropdownOpen}
                                className={styles.dropdown}
                                items={[
                                    {
                                        id: 0,
                                        title: messages['userMenu.profile'],
                                        onClick: goToProfile,
                                    },
                                    {
                                        id: 1,
                                        title: messages['userMenu.pairDevice'],
                                        onClick: pairDevice,
                                    },
                                    ...(canManagePairedDevices ? [{
                                        id: 2,
                                        title: messages['userMenu.pairedDevices'],
                                        onClick: goToPairedDevices,
                                    }] : []),
                                    {
                                        id: 3,
                                        title: messages['userMenu.requestOtp'],
                                        onClick: requestOtp,
                                    },
                                    // LMS export and LTI registration are company-wide settings.
                                    ...(userPermissionsUIStore.isAdminOrHigher ? [{
                                        id: 4,
                                        title: messages['userMenu.integrations'],
                                        onClick: openIntegrations,
                                    }] : []),
                                    {
                                        id: 5,
                                        title: messages['userMenu.logout'],
                                        onClick: signOutUser,
                                    }
                                ]}
                            />
                        );
                    }}
                </Observer>
            </ClickOutsideListener>
        </section>
    );
}
